import React from 'react';
import { Link } from 'react-router-dom';
import B2BLayout from '../components/b2b/B2BLayout';
import { AlertModal, IconSpecial } from '../components/ui';
import { useAppContext } from '../context/AppContext';
import { apiFetch } from '../services/apiClient';
import './InquiryPage.scss';

const REQUIRED_FIELDS = [
  { key: 'company_name', label: '회사명', emptyMessage: '회사명을 입력해 주세요.' },
  { key: 'contact_name', label: '이름', emptyMessage: '이름을 입력해 주세요.' },
  { key: 'phone', label: '연락처', emptyMessage: '연락처를 입력해 주세요.' },
  { key: 'email', label: '이메일', emptyMessage: '이메일을 입력해 주세요.', invalidMessage: '올바른 이메일 형식을 입력해 주세요.' },
];

const INITIAL_FORM = {
  company_name: '',
  contact_name: '',
  phone: '',
  email: '',
  message: '',
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function InquiryPage() {
  const { isLoggedIn, sessionChecked } = useAppContext();
  const [form, setForm] = React.useState(INITIAL_FORM);
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [successModalOpen, setSuccessModalOpen] = React.useState(false);
  const fieldRefs = React.useRef({});

  const getFieldError = React.useCallback((key, value) => {
    const field = REQUIRED_FIELDS.find((item) => item.key === key);
    if (!field) return '';
    const trimmed = String(value || '').trim();
    if (!trimmed) return field.emptyMessage;
    if (key === 'email' && !isValidEmail(trimmed)) return field.invalidMessage;
    return '';
  }, []);

  const isFormComplete = React.useMemo(
    () => REQUIRED_FIELDS.every((field) => !getFieldError(field.key, form[field.key])),
    [form, getFieldError],
  );

  const validateForm = React.useCallback(() => {
    const nextErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const message = getFieldError(field.key, form[field.key]);
      if (message) nextErrors[field.key] = message;
    });
    return nextErrors;
  }, [form, getFieldError]);

  const focusFirstInvalidField = React.useCallback((nextErrors) => {
    const firstInvalid = REQUIRED_FIELDS.find((field) => nextErrors[field.key]);
    if (!firstInvalid) return;
    fieldRefs.current[firstInvalid.key]?.focus();
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleBlur = (key) => {
    const message = getFieldError(key, form[key]);
    setErrors((prev) => {
      if (!message) {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: message };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      focusFirstInvalidField(nextErrors);
      return;
    }
    if (!isFormComplete || submitting) return;

    setSubmitting(true);
    try {
      const response = await apiFetch('/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || '이용 문의를 접수하지 못했습니다.');
      }
      setForm(INITIAL_FORM);
      setErrors({});
      setSuccessModalOpen(true);
    } catch (error) {
      setErrors({ submit: error.message || '이용 문의 접수 중 오류가 발생했습니다.' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = ({ key, label }) => (
    <div className="inquiry-page__field" key={key}>
      <input
        ref={(node) => {
          fieldRefs.current[key] = node;
        }}
        id={`inquiry-${key}`}
        name={key}
        value={form[key]}
        onChange={(event) => handleChange(key, event.target.value)}
        onBlur={() => handleBlur(key)}
        placeholder={`${label} *`}
        aria-invalid={Boolean(errors[key])}
        aria-describedby={errors[key] ? `inquiry-${key}-error` : undefined}
        className={errors[key] ? 'has-error' : ''}
      />
      {errors[key] ? (
        <p id={`inquiry-${key}-error`} className="inquiry-page__error" role="alert">
          {errors[key]}
        </p>
      ) : null}
    </div>
  );

  const page = (
    <div className={`inquiry-page${isLoggedIn ? '' : ' inquiry-page--public'}`}>
      {!isLoggedIn ? (
        <header className="inquiry-page__public-header">
          <Link to="/" className="inquiry-page__public-logo" aria-label="ReOpsAI">
            <IconSpecial name="logo" className="inquiry-page__public-logo-img" />
          </Link>
        </header>
      ) : null}
      <main className="inquiry-page__content">
        <header className="inquiry-page__hero">
          <h1>ReOpsAI 이용 문의</h1>
          <p>
            팀 환경에 맞는 도입 방식과 활용 시나리오를 빠르게 안내드립니다.
            아래 항목을 남겨주시면 확인 후 연락드리겠습니다.
          </p>
        </header>

        <form className="inquiry-page__form" onSubmit={handleSubmit} noValidate>
          {REQUIRED_FIELDS.map(renderField)}
          <div className="inquiry-page__field">
            <textarea
              id="inquiry-message"
              name="message"
              value={form.message}
              onChange={(event) => handleChange('message', event.target.value)}
              placeholder="문의사항"
            />
          </div>
          {errors.submit ? (
            <p className="inquiry-page__error inquiry-page__error--submit" role="alert">
              {errors.submit}
            </p>
          ) : null}
          <div className="inquiry-page__actions">
            <button type="submit" disabled={!isFormComplete || submitting}>
              {submitting ? '접수 중...' : '문의하기'}
            </button>
          </div>
        </form>
      </main>

      <AlertModal
        isOpen={successModalOpen}
        type="success"
        title="문의 접수 완료"
        message="이용 문의가 접수되었습니다. 빠르게 연락드리겠습니다."
        autoClose={false}
        onClose={() => setSuccessModalOpen(false)}
      />
    </div>
  );

  if (!sessionChecked) {
    return (
      <div className="inquiry-page inquiry-page--loading">
        <p>로딩 중...</p>
      </div>
    );
  }

  if (!isLoggedIn) return page;

  return (
    <B2BLayout hideCollapseButton={true} defaultCollapsed={false} hidePageHeader={true}>
      {page}
    </B2BLayout>
  );
}

export default InquiryPage;
